/**
 * The experiment switches — the two decisions PHASE0-RESULTS.md marks as open
 * gates, kept OPEN in the build so they can be decided on a device instead of
 * by whoever edited the mapping last.
 *
 *   trigger      Gate 4: 'quantized' books discrete triggers on the transport
 *                grid; 'immediate' fires them on the spot. The vocabulary doc's
 *                core rule ("gesture shapes what is sounding; the grid decides
 *                when it starts") makes quantized the default until the gate
 *                is measured.
 *   cutoffSource Gate 8: which axis drives filter cutoff. 'spread' is option A
 *                (decoupled); 'aperture' is option B/C (the coupled
 *                articulation — closing toward the trigger darkens the note).
 *
 * In-memory only — nothing persists between loads, by design. Pure module:
 * no DOM, no audio; the UI writes it, mappers and scenes read it.
 */

export interface ExperimentConfig {
  trigger: 'quantized' | 'immediate';
  cutoffSource: 'spread' | 'aperture';
  /** The vocabulary doc's cutoff range: 180 Hz → 9 kHz, log-mapped. */
  cutoffLoHz: number;
  cutoffHiHz: number;
}

export const DEFAULT_EXPERIMENTS: ExperimentConfig = {
  trigger: 'quantized',
  cutoffSource: 'spread',
  cutoffLoHz: 180,
  cutoffHiHz: 9000,
};

let current: ExperimentConfig = { ...DEFAULT_EXPERIMENTS };
const listeners = new Set<(c: ExperimentConfig) => void>();

export function getExperiments(): ExperimentConfig {
  return current;
}

export function setExperiments(patch: Partial<ExperimentConfig>): void {
  current = { ...current, ...patch };
  for (const cb of listeners) cb(current);
}

/** Subscribe to changes — the control log records each one as an event line. */
export function onExperimentsChange(cb: (c: ExperimentConfig) => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

/** Test hook: back to defaults, listeners kept. */
export function resetExperiments(): void {
  current = { ...DEFAULT_EXPERIMENTS };
}
